import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { resetStatisticsSession } from '../api/statisticsQueries';
import { clearDocsSessionCookie, setDocsSessionCookie } from '../utils/docsSession';

const AuthContext = createContext(null);
let cacheSessionSequence = 0;

function roleFromToken(token) {
  try {
    const encoded = String(token).split('.')[1];
    const payload = JSON.parse(window.atob(encoded.replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.role || 'USER').toLowerCase();
  } catch {
    return 'user';
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Shared opaque cache scope survives page navigation and changes with credentials.
  const cacheSession = useMemo(() => token ? ++cacheSessionSequence : null, [token]);

  useEffect(() => {
    const stored = window.localStorage.getItem('fancian-admin-auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const nextToken = parsed.token || null;
        const user = parsed.user && nextToken ? { ...parsed.user, role: roleFromToken(nextToken) } : null;
        setUser(user);
        setToken(nextToken);
        if (nextToken) setDocsSessionCookie(nextToken);
        else clearDocsSessionCookie();
      } catch {
        window.localStorage.removeItem('fancian-admin-auth');
        clearDocsSessionCookie();
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!loading) {
      const payload = user && token ? { user, token } : null;
      if (payload) {
        window.localStorage.setItem('fancian-admin-auth', JSON.stringify(payload));
        setDocsSessionCookie(token);
      } else {
        window.localStorage.removeItem('fancian-admin-auth');
        clearDocsSessionCookie();
      }
    }
  }, [user, token, loading]);

  const login = (nextUser, nextToken) => {
    resetStatisticsSession();
    setDocsSessionCookie(nextToken);
    setUser(nextUser);
    setToken(nextToken);
  };

  const logout = () => {
    resetStatisticsSession();
    clearDocsSessionCookie();
    setUser(null);
    setToken(null);
  };

  const value = useMemo(
    () => ({
      user,
      token,
      cacheSession,
      role: user?.role ?? null,
      loading,
      login,
      logout,
      isAuthenticated: Boolean(user && token),
    }),
    [user, token, loading, cacheSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
